import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Banner, Button, Card, EmptyState, Field, Input } from "@magarac/ui";
import {
  locationListSchema,
  locationSchema,
  type CreateLocationInput,
} from "@magarac/shared/schemas/resource";
import { HelpIcon } from "../../../components/HelpIcon";
import { api } from "../../../lib/api";
import { messageFor } from "../../../lib/errors";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/settings/locations")({
  component: LocationsPage,
});

const emptyAddress = { line1: "", city: "", state: "", postalCode: "", country: "US" as const };

/**
 * Base address (field mode: where the day starts) and shop locations (appointment mode).
 * Screen key: settings.locations
 */
function LocationsPage() {
  const { active } = useTenant();
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ["locations", active?.id],
    queryFn: () => api.get("/locations", locationListSchema),
    enabled: Boolean(active),
  });
  const [name, setName] = useState("");
  const [address, setAddress] = useState({ ...emptyAddress });
  const [error, setError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["locations", active?.id] });

  const create = useMutation({
    mutationFn: () => {
      const input: CreateLocationInput = {
        name,
        address: { ...address, state: address.state.toUpperCase() },
      };
      return api.post("/locations", input, z.object({ location: locationSchema }));
    },
    onSuccess: async () => {
      setName("");
      setAddress({ ...emptyAddress });
      setError(null);
      await refresh();
    },
    onError: (err) => setError(messageFor(err)),
  });
  const makeBase = useMutation({
    mutationFn: (id: string) =>
      api.patch(`/locations/${id}`, { isBase: true }, z.object({ location: locationSchema })),
    onSuccess: refresh,
  });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Locations</h1>
        <HelpIcon screen="settings.locations" />
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <Card title="Add a location" className="mb-6">
        <p className="mb-3 text-sm text-muted">
          Your main address is where the day starts for drive-time estimates. Shops and salons use
          locations for appointments.
        </p>
        <Field id="loc-name" label="Name">
          <Input
            id="loc-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Shop"
          />
        </Field>
        <Field id="line1" label="Street address">
          <Input
            id="line1"
            autoComplete="address-line1"
            value={address.line1}
            onChange={(e) => setAddress({ ...address, line1: e.target.value })}
          />
        </Field>
        <div className="grid gap-2 md:grid-cols-[2fr_1fr_1fr]">
          <Field id="city" label="City">
            <Input
              id="city"
              autoComplete="address-level2"
              value={address.city}
              onChange={(e) => setAddress({ ...address, city: e.target.value })}
            />
          </Field>
          <Field id="state" label="State">
            <Input
              id="state"
              autoComplete="address-level1"
              maxLength={2}
              value={address.state}
              onChange={(e) => setAddress({ ...address, state: e.target.value.toUpperCase() })}
            />
          </Field>
          <Field id="zip" label="ZIP">
            <Input
              id="zip"
              autoComplete="postal-code"
              inputMode="numeric"
              value={address.postalCode}
              onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
            />
          </Field>
        </div>
        <Button
          onClick={() => create.mutate()}
          busy={create.isPending}
          disabled={
            !name ||
            !address.line1 ||
            !address.city ||
            address.state.length !== 2 ||
            !address.postalCode
          }
        >
          Add location
        </Button>
      </Card>
      <Card title="Your locations">
        {list.data && list.data.locations.length === 0 ? (
          <EmptyState
            title="No locations yet"
            body="Add your main address so drive times can be estimated."
          />
        ) : (
          <ul>
            {list.data?.locations.map((l) => (
              <li
                key={l.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-line py-3 last:border-0"
              >
                <div>
                  <div className="font-medium">
                    {l.name}{" "}
                    {l.isBase ? (
                      <span className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700">
                        Main
                      </span>
                    ) : null}
                  </div>
                  <div className="text-sm text-muted">
                    {l.address.line1}, {l.address.city}, {l.address.state} {l.address.postalCode}
                    {l.lat === null ? (
                      <span className="text-warn"> · address not found on the map</span>
                    ) : null}
                  </div>
                </div>
                {!l.isBase ? (
                  <Button variant="secondary" onClick={() => makeBase.mutate(l.id)}>
                    Make main
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
